import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Category, PostSummary } from '@/types'

import { getCategories, getPosts } from '@/lib/journal'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Eyebrow } from '@/components/generals/eyebrow'
import { ContactCta } from '@/components/generals/contact-cta'
import { JournalListing } from '@/components/journal/journal-listing'

interface JournalPageProps {
    posts: PostSummary[]
    categories: Category[]
    failed: boolean
}

export const getServerSideProps: GetServerSideProps<JournalPageProps> = async () => {
    try {
        const [posts, categories] = await Promise.all([getPosts(), getCategories()])

        return { props: { posts, categories, failed: false } }
    }
    catch (error) {
        console.error('The journal could not load its stories:', error instanceof Error ? error.message : error)

        return { props: { posts: [], categories: [], failed: true } }
    }
}

const JournalPage: FC<JournalPageProps> = ({
    posts,
    categories,
    failed
}): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Journal"
                description="Practical stories on layout, light, color and materials for warm, lived-in homes: living rooms, kitchens, bedrooms and the finishing touches."
            />

            <section id='journal' className='mx-auto max-w-7xl px-5 pt-14 pb-8 sm:px-8 lg:pt-20'>
                <Eyebrow>The journal</Eyebrow>

                <h1 className='mt-6 max-w-4xl font-heading text-5xl leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl'>
                    Notes on making a home
                </h1>

                <p className='mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl'>
                    Practical ideas on layout, light, color and materials, written in the studio for the rooms you live in every day.
                </p>
            </section>

            <section id='stories' aria-label="Stories" className='mx-auto max-w-7xl px-5 pt-6 sm:px-8'>
                {failed ? (
                    <div className='rounded-2xl border border-dashed border-border px-6 py-16 text-center'>
                        <p className='font-heading text-2xl'>
                            The stories could not be loaded right now
                        </p>

                        <p className='mt-3 text-muted-foreground'>
                            Please refresh the page in a moment.
                        </p>
                    </div>
                ) : (
                    <JournalListing
                        posts={posts}
                        categories={categories}
                        totalCount={posts.length}
                    />
                )}
            </section>

            <ContactCta />
        </Layout>
    )
}

export default JournalPage
